import React, { useEffect, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../lib/constants';

/**
 * Bottom-left sidebar widget: live MongoDB Atlas connection indicator.
 *
 * • Polls /api/system/db-status every 30s
 * • Always shows the green/red dot + label + latency
 * • Owners can click to expand and see redacted connection string
 */
export default function DbStatusBadge() {
  const { isOwner } = useAuth();
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await fetch(`${API_URL}/api/system/db-status`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setStatus(d);
      } catch {
        if (!cancelled) setStatus({ connected: false });
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (!status) return null;

  const dot = status.connected ? '#10b981' : '#dc2626';
  const label = status.connected ? 'DB Connected' : 'DB Offline';

  return (
    <div
      className="db-status-badge"
      data-testid="db-status-badge"
      onClick={() => isOwner && setExpanded(!expanded)}
      style={{
        cursor: isOwner ? 'pointer' : 'default',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 8,
        padding: '8px 10px',
        marginBottom: 8,
        fontSize: 11,
        color: '#cbd5e1',
      }}
      title={isOwner ? 'Click to view connection details' : ''}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: dot, boxShadow: `0 0 6px ${dot}`,
          }}
        />
        <span style={{ fontWeight: 600 }}>{label}</span>
        {status.latency_ms !== undefined && (
          <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 10 }}>
            {status.latency_ms}ms
          </span>
        )}
      </div>

      {isOwner && expanded && status.connection_string_redacted && (
        <div
          style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}
          data-testid="db-status-details"
        >
          <Detail label="Provider" value={status.provider} />
          <Detail label="Host" value={status.host || '—'} mono />
          <Detail label="Database" value={status.database || '—'} mono />
          <div style={{ opacity: 0.6, fontSize: 10, marginBottom: 4 }}>
            Connection string (password redacted)
          </div>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 10,
              wordBreak: 'break-all',
              background: 'rgba(0,0,0,0.3)',
              padding: '6px 8px',
              borderRadius: 4,
              userSelect: 'all',
            }}
          >
            {status.connection_string_redacted}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <>
      <div style={{ opacity: 0.6, fontSize: 10, marginBottom: 4 }}>{label}</div>
      <div
        style={{
          fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
          wordBreak: 'break-all',
          marginBottom: 6,
        }}
      >
        {value}
      </div>
    </>
  );
}
